import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../../assets/environments/environment';
import {commonProperties} from '../../../assets/environments/environment.common';
import {MaskedItemResourceModel} from '../../request/models/masked-item-resource.model';
import {ListContentModel} from '@i-supplier/angular-shared-module/lib/models/list-content.model';
import {CommentModel} from '../models/comment.model';
import {EnveloppeGetResource} from '../models/enveloppe-get-resource.model';
import {OnCallConsumptionResource} from '../models/on-call-consumption-resource.model';
import {PurchaseOrderAdvancedSearch} from '../models/purchase-order-advanced-search.model';
import {PurchaseOrderModel} from '../models/purchase-order.model';
import {PurchaseOrderStatusEnum} from '../models/Purchase-order.status.enum';


@Injectable({
  providedIn: 'root'
})
export class PurchaseOrderService {
  constructor(private _httpClient: HttpClient) {}

  purchaseOrdersApi = environment.api_root + commonProperties.purchaseOrdersList;

  mapAdvancedSearchForm(params: any): PurchaseOrderAdvancedSearch {
    return new PurchaseOrderAdvancedSearch(params);
  }

  getPurchaseOrdersByPage(page: number, size: number, sort: string, search: string, advancedSearch: PurchaseOrderAdvancedSearch): Observable<ListContentModel<any>> {
    let params = new HttpParams().set('size', size.toString()).set('page', page.toString()).set('sort', sort);
    if (search) {
      params = params.set('search', search);
    }
    if (advancedSearch.supplierName) {
      params = params.set('supplierName', advancedSearch.supplierName);
    }
    if (advancedSearch.purchaseOrderNumber) {
      params = params.set('purchaseOrderNumber', advancedSearch.purchaseOrderNumber);
    }
    if (advancedSearch.amendmentNumber) {
      params = params.set('amendmentNumber', advancedSearch.amendmentNumber);
    }
    if (advancedSearch.requestNumber) {
      params = params.set('requestNumber', advancedSearch.requestNumber);
    }
    if (advancedSearch.status && advancedSearch.status !== PurchaseOrderStatusEnum.ALL) {
      if (Array.isArray(advancedSearch.status)) {
        advancedSearch.status.forEach(status => {
          params = params.append('status', status);
        });
      } else {
        params = params.set('status', advancedSearch.status);
      }
    }
    return this._httpClient.get<ListContentModel<any>>(`${environment.api_root}purchaseOrderMgtApi/purchase-orders`, {params});
  }

  getPurchaseOrderById(purchaseOrderId: string): Observable<PurchaseOrderModel> {
    return this._httpClient.get<PurchaseOrderModel>(environment.api_root + commonProperties.purchaseOrderById.replace(':purchaseOrderId', purchaseOrderId));
  }

  getComments(): Observable<CommentModel[]> {
    return this._httpClient.get<CommentModel[]>(environment.api_root + commonProperties.comments);
  }

  getPurchaseOrderFile(purchaseOrderId: string) {
    const headers = new HttpHeaders().set('Accept', 'application/pdf, application/json');
    return this._httpClient.get<Blob>(environment.api_root + commonProperties.purchaseOrderFileById.replace(':purchaseOrderId', purchaseOrderId), {
      observe: 'response',
      headers: headers,
      responseType: 'blob' as 'json'
    });

  }

  mapCommentFormValueToResource(formValue: Partial<CommentModel>): CommentModel {
    return new CommentModel(formValue);
  }

  createComment(commentBody: CommentModel) {
    return this._httpClient.post(environment.api_root + commonProperties.commentCreation, commentBody);
  }

  editComment(commentId: string, comment: CommentModel): Observable<any> {
    const url = environment.api_root + commonProperties.commentById.replace(':commentId', commentId);
    return this._httpClient.patch(url, comment);
  }

  removeComment(commentId: any): Observable<any> {
    const url = environment.api_root + commonProperties.commentById.replace(':commentId', commentId);
    return this._httpClient.delete(url);
  }

  actionToPurchaseOrder(purchaseOrderId: string, body: any): Observable<any> {
    return this._httpClient.put(environment.api_root + commonProperties.purchaseOrderActions.replace(':purchaseOrderId', purchaseOrderId), body);
  }

  getPurchaseOrderSignedFile(purchaseOrderId: string) {
    const url = `${this.purchaseOrdersApi}/${purchaseOrderId}/signed`;
    const headers = new HttpHeaders();
    const options = {
      headers: headers.set('Accept', 'application/pdf'),
      responseType: 'blob' as 'json'
    };
    return this._httpClient.get<Blob>(url, options);
  }

  postSignedPurchaseOrder(params: { id: any; fileName: any; fileContent: any; }): Observable<any> {
    return this._httpClient.post(
      environment.api_root + commonProperties.purchaseOrderPostSigned.replace(':purchaseOrderId', params.id),
      {fileName: params.fileName, fileContent: params.fileContent}
    );
  }

  getPurchaseOrdersWaitingForRequest(requestId: string): Observable<any> {
    return this._httpClient.get(environment.api_root + 'purchaseOrderMgtApi/purchase-orders/request/' + requestId + '/waiting');
  }

  getRequestEnveloppe(requestId: string, type: string, providerId?: string): Observable<EnveloppeGetResource[]> {
    return this._httpClient.get<EnveloppeGetResource[]>(environment.api_root + 'purchaseOrderMgtApi/envelope/' + requestId + '/' + type + (providerId ? ('?providerId=' + providerId) : ''));
  }

  getMinimumRequestEnveloppe(requestId: string, type: string, providerId?: string): Observable<number> {
    return this._httpClient.get<number>(environment.api_root + 'purchaseOrderMgtApi/envelope/minimum/' + requestId + '/' + type + (providerId ? ('?providerId=' + providerId) : ''));
  }

  getOnCallConsumption(requestId: string): Observable<OnCallConsumptionResource> {
    return this._httpClient.get<OnCallConsumptionResource>(environment.api_root + 'purchaseOrderMgtApi/envelope/on-call-consumption/' + requestId);
  }

  updateMaskedLine(purchaseOrderId: string, params: MaskedItemResourceModel[]): Observable<PurchaseOrderModel> {
    return this._httpClient.patch<PurchaseOrderModel>(`${environment.api_root}purchaseOrderMgtApi/purchase-orders/${purchaseOrderId}/masked`, params);
  }

  generateSignatureLinks(id: string): any {
    return this._httpClient.post(environment.api_root + 'purchaseOrderMgtApi/purchase-orders/' + id + '/generate-signature-links', {});
  }
}
